import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import BondNetwork from "./BondNetwork";
import AppDownloadButtons from "./AppDownloadButtons";
import { IS_APP_STORE_LIVE } from "../config/appLinks";

/**
 * Home — hero section.
 *
 * Launch focus is P2P matching and community tips. The right side shows those
 * two screens; copy and CTAs stay on that pair.
 */

const SUBLINES = [
  "Peer-to-peer matching on real fixtures.",
  "Community tips you can read and share.",
  "Funds in escrow until the rule clears.",
  "A clean, disclosed 7% fee.",
];

const HERO_SCREENS = {
  p2p: {
    name: "P2P",
    src: "/1242x2688/03_P2P.png",
    accent: "from-[#1E88E5]/40",
  },
  tips: {
    name: "Tips",
    src: "/1242x2688/04_Predictions.png",
    accent: "from-[#64B5F6]/25",
  },
};

const ScreenCard = ({
  screen,
  className = "",
  imageClassName = "",
  priority = false,
}) => (
  <div
    className={`group relative overflow-hidden rounded-[2rem] border border-white/10 bg-white/[0.035] p-2 shadow-[0_28px_90px_-36px_rgba(30,136,229,0.75)] transition duration-500 hover:-translate-y-2 hover:scale-[1.015] ${className}`}
  >
    <div
      className={`absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b ${screen.accent} to-transparent opacity-80 blur-2xl`}
      aria-hidden
    />
    <img
      src={screen.src}
      alt={`${screen.name} BetBond app preview`}
      loading={priority ? "eager" : "lazy"}
      decoding="async"
      className={`relative z-10 w-full rounded-[1.55rem] object-cover ring-1 ring-white/10 transition duration-500 group-hover:brightness-110 ${imageClassName}`}
    />
  </div>
);

const Home = () => {
  const [sublineIdx, setSublineIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => {
      setSublineIdx((i) => (i + 1) % SUBLINES.length);
    }, 3400);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="relative w-full min-h-screen overflow-hidden bg-black">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(60% 50% at 50% 30%, rgba(30,136,229,0.12), transparent 70%), linear-gradient(to bottom, #000 0%, #050608 60%, #000 100%)",
        }}
      />
      <BondNetwork className="opacity-70" />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(120% 80% at 50% 100%, rgba(0,0,0,0.7), transparent 50%)",
        }}
      />

      <main className="relative w-full min-h-screen pt-20 md:pt-24 pb-16">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 items-center gap-10 lg:gap-12">
            <div className="lg:col-span-6 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/[0.03] backdrop-blur-sm">
                <span className="relative flex w-1.5 h-1.5">
                  <span className="absolute inset-0 rounded-full bg-[#1E88E5] opacity-70 animate-ping" />
                  <span className="relative rounded-full w-1.5 h-1.5 bg-[#1E88E5]" />
                </span>
                <span className="text-[10px] uppercase tracking-[0.22em] text-gray-400">
                  P2P matching · Community tips
                </span>
              </div>

              <h1 className="mt-5 font-extrabold tracking-tight leading-[0.95] text-white text-5xl sm:text-6xl md:text-7xl">
                Make
                <br />
                a{" "}
                <span className="relative inline-block">
                  <span className="text-[#1E88E5]">Bond.</span>
                  <span
                    aria-hidden
                    className="absolute left-0 right-2 -bottom-1 h-[3px] rounded-full"
                    style={{
                      background:
                        "linear-gradient(90deg, rgba(30,136,229,0) 0%, rgba(30,136,229,0.9) 50%, rgba(30,136,229,0) 100%)",
                      filter: "blur(0.5px)",
                    }}
                  />
                </span>
              </h1>

              <div className="mt-5 min-h-[1.75rem] flex justify-center lg:justify-start">
                <AnimatePresence mode="wait">
                  <motion.p
                    key={sublineIdx}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.45 }}
                    className="text-gray-400 text-base sm:text-lg"
                  >
                    {SUBLINES[sublineIdx]}
                  </motion.p>
                </AnimatePresence>
              </div>

              <p className="mt-5 max-w-md mx-auto lg:mx-0 text-sm leading-relaxed text-gray-500">
                Post or join a sports offer. Someone takes the other side. Escrow
                holds the stakes until the match settles. Read community tips
                before you lock in.
              </p>

              <AppDownloadButtons
                showTooltips
                className="mt-8 lg:justify-start"
              />

              <div className="mt-6 flex flex-wrap items-center justify-center lg:justify-start gap-x-5 gap-y-2 text-[11px] uppercase tracking-[0.18em] text-gray-600">
                <Link
                  to="/app/p2p"
                  className="hover:text-[#64B5F6] transition-colors"
                >
                  Open P2P
                </Link>
                <span className="hidden sm:inline text-white/15">·</span>
                <Link
                  to="/app/predictions"
                  className="hover:text-[#64B5F6] transition-colors"
                >
                  Open Tips
                </Link>
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="relative mx-auto h-[420px] w-full max-w-[420px] sm:h-[500px] sm:max-w-[480px]">
                <div
                  className="absolute left-1/2 top-1/2 h-[85%] w-[90%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#1E88E5]/25 blur-[90px]"
                  aria-hidden
                />
                <Link
                  to="/app/predictions"
                  className="absolute left-0 top-8 z-10 w-[48%] rotate-[-8deg] transition duration-500 hover:rotate-[-3deg] hover:z-20"
                  aria-label="Open Tips"
                >
                  <ScreenCard
                    screen={HERO_SCREENS.tips}
                    className="w-full"
                    imageClassName="aspect-[1242/2688]"
                  />
                </Link>
                <Link
                  to="/app/p2p"
                  className="absolute right-0 top-0 z-20 w-[58%] rotate-[4deg] transition duration-500 hover:rotate-0"
                  aria-label="Open P2P"
                >
                  <ScreenCard
                    screen={HERO_SCREENS.p2p}
                    priority
                    className="w-full"
                    imageClassName="aspect-[1242/2688]"
                  />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Tooltip
        id="apple-tooltip"
        place="top"
        content={
          IS_APP_STORE_LIVE
            ? "Download on the App Store"
            : "Use BetBond on iPhone in Safari"
        }
      />
      <Tooltip id="android-tooltip" place="top" content="Download official Android APK" />
      <Tooltip id="web-tooltip" place="top" content="Use BetBond in your browser" />
    </div>
  );
};

export default Home;
